import React, {useEffect, useRef, useState} from 'react'
import Moment from 'react-moment'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'
import {useProjectGuest} from './ProjectGuestContext'
import {ProjectGuestWeekPanel} from './ProjectGuestWeekPanel'

type PanelKind = 'info' | 'users' | 'week' | null

const PROJECT_TEAL = '#00838f'

// Project.Color is free text from the DB, so it can arrive as '00838f' (no #), empty, or junk.
// A single invalid token makes the browser discard an entire shorthand declaration — which is
// how a bad colour silently removed the day-circle border and the icon tile's tint.
function safeColor(value?: string) {
  const candidate = (value || '').trim()
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(candidate) ? candidate : PROJECT_TEAL
}

/** 10% tint of a 6-digit hex, for the icon tile background. */
function tint(hex: string) {
  return /^#[0-9a-f]{6}$/i.test(hex) ? `${hex}1a` : `${PROJECT_TEAL}1a`
}

// Mon–Fri always render so the row keeps a stable width; Sat/Sun are appended only when the
// project actually runs on them, rather than padding every card with two dead circles.
const WEEKDAYS = [
  {key: 'Monday', label: 'M'},
  {key: 'Tuesday', label: 'T'},
  {key: 'Wednesday', label: 'W'},
  {key: 'Thursday', label: 'T'},
  {key: 'Friday', label: 'F'},
]
const WEEKEND = [
  {key: 'Saturday', label: 'S'},
  {key: 'Sunday', label: 'S'},
]

/** Elapsed share of the project's calendar window: day 50 of 100 → 50%. */
function elapsedPercent(startDate?: string, endDate?: string) {
  if (!startDate || !endDate) return 0

  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()
  const now = Date.now()

  if (isNaN(start) || isNaN(end) || end <= start) return 0
  if (now <= start) return 0
  if (now >= end) return 100

  return Math.round(((now - start) / (end - start)) * 100)
}

// Derived from the date window rather than Project.Status, so the badge and the progress bar
// can never disagree — see the note in the handover if you'd rather this read Status instead.
function scheduleStatus(startDate?: string, endDate?: string) {
  const percent = elapsedPercent(startDate, endDate)

  if (percent >= 100) return {label: 'Completed', badge: 'badge-light-success'}
  if (percent <= 0) return {label: 'Not Started', badge: 'badge-light'}
  return {label: 'In Progress', badge: 'badge-light-primary'}
}

function ProjectInfoPanel({project}: {project: any}) {
  return (
    <div className='row g-6'>
      <div className='col-md-6'>
        <div className='fw-bolder text-dark'>Site(s)</div>
        <div className='text-gray-600 mb-5'>
          {(project.sites || []).length > 0 ? (
            (project.sites || []).map((s: any) => s.siteName).join(', ')
          ) : (
            <i>No sites assigned</i>
          )}
        </div>

        <div className='fw-bolder text-dark'>Project Owner</div>
        <div className='text-gray-600'>{project.ownerName || <i>Not set</i>}</div>
      </div>

      <div className='col-md-6'>
        <div className='fw-bolder text-dark'>Description</div>
        <div className='text-gray-600 mb-5'>{project.description || <i>No description</i>}</div>

        <div className='fw-bolder text-dark'>Instructions</div>
        <div className='text-gray-600' style={{whiteSpace: 'pre-wrap'}}>
          {project.instructions || <i>No instructions</i>}
        </div>
      </div>
    </div>
  )
}

function ProjectUsersPanel({project}: {project: any}) {
  const users: any[] = project.users || []
  const color = safeColor(project.color)

  if (users.length === 0) {
    return <div className='text-center text-muted py-5'>No team members found</div>
  }

  return (
    <div className='table-responsive'>
      <table className='table align-middle gs-0 gy-3 mb-0'>
        <thead>
          <tr className='fw-bold text-muted bg-light'>
            <th className='ps-4 min-w-200px rounded-start'>Name</th>
            <th className='min-w-150px'>Role</th> 
          </tr>
        </thead>
        <tbody>
          {users.map((user: any) => (
            <tr key={user.id}>
              <td className='ps-4 text-dark fw-bold'>{user.userName}</td>
              <td className='text-gray-600'>{user.projectRoleName || '—'}</td> 
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ProjectRow({
  project,
  openPanel,
  onTogglePanel,
  dimmed,
}: {
  project: any
  openPanel: PanelKind
  onTogglePanel: (panel: PanelKind) => void
  dimmed: boolean
}) {
  const activeDays: string[] = (project.daysOfWeek || []).map((d: any) => String(d))
  const days = [...WEEKDAYS, ...WEEKEND.filter((d) => activeDays.includes(d.key))]

  const percent = elapsedPercent(project.startDate, project.endDate)
  const status = scheduleStatus(project.startDate, project.endDate)
  const color = safeColor(project.color)
  const userCount = project.users?.length ?? 0

  return (
    <div
      className='card mb-4'
      style={{
        // Dimmed rather than disabled: the other rows stay clickable so you can move the
        // open panel straight to another project.
        opacity: dimmed ? 0.4 : 1,
        transition: 'opacity .15s ease-in-out',
      }}
    >
      {/* `gap` sets the row gap as well as the column gap, so gap-20 (5rem) also puts 80px of
          dead vertical space between the items once they wrap on a narrow screen. Keep the wide
          spacing only from lg up, and stay tight below it. */}
      {/* Anywhere on the row except the two icon buttons opens the week planner. The icons
          stop propagation via their own onClick, which fires first. */}
      <div
        className='card-body d-flex align-items-center flex-wrap gap-3 gap-lg-20 py-4 justify-content-center justify-content-lg-start'
        role='button'
        tabIndex={0}
        aria-expanded={openPanel === 'week'}
        aria-label={`Plan my week on ${project.name}`}
        style={{cursor: 'pointer'}}
        onClick={() => onTogglePanel('week')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onTogglePanel('week')
          }
        }}
      >
        {/* Name, days and dates read left-aligned at every size, even while the row's other
            items centre on a narrow screen. */}
        <div className='d-flex align-items-center gap-4 text-start' style={{minWidth: '260px'}}>
          <div
            className='d-flex flex-center rounded'
            style={{width: 52, height: 52, backgroundColor: tint(color), flexShrink: 0}}
          >
            <i
              className='fa-duotone fa-solid fa-calendar-circle-user fs-2x'
              style={{color}}
            />
          </div>

          <div>
            <div className='fs-4 fw-bold text-dark'>{project.name}</div>

            <div className='d-flex align-items-center gap-1 my-1'>
              {days.map((day, index) => {
                const active = activeDays.includes(day.key.toLowerCase())
                return (
                  <span
                    key={`${day.key}-${index}`}
                    className='d-flex flex-center fs-8'
                    title={day.key}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      // Longhand, not the `border` shorthand: if borderColor is ever invalid
                      // only that one property is discarded, so the ring still renders.
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: active ? color : '#b5b5c3',
                      color: active ? color : '#b5b5c3',
                      fontWeight: active ? 600 : 400,
                      lineHeight: 1,
                    }}
                  >
                    {day.label}
                  </span>
                )
              })}
            </div>

            <div className='text-gray-600 fs-7'>
              {project.startDate && <Moment utc local format='DD/MM/YYYY'>{project.startDate}</Moment>}
              {' to '}
              {project.endDate && <Moment utc local format='DD/MM/YYYY'>{project.endDate}</Moment>}
            </div>
          </div>
        </div>

        {/* The two icons are one flex item, not two, so the wrapping row can never split them
            apart — they share a baseline on a wide screen and drop to the next line together on
            a narrow one. btn-icon's fixed square sizing also left the glyphs sitting off-centre
            beside the taller blocks either side, so both buttons centre their own contents and
            share a height. */}
        <div className='d-flex align-items-center justify-content-center gap-3'>
          {/* Details — site, owner, description, instructions — in the panel below. */}
          <button
            type='button'
            className='btn btn-icon d-flex align-items-center justify-content-center p-0'
            style={{height: 52}}
            title='Project details'
            aria-expanded={openPanel === 'info'}
            onClick={(e) => {
              e.stopPropagation()
              onTogglePanel('info')
            }}
          >
            <i className='fa-duotone fa-solid fa-memo-circle-info fs-2x' style={{color: color}} />
          </button>

          {/* Member count — the roster itself opens in the panel below. The count sits on the
              icon's own centre line rather than its cap height (it used to be align-top). */}
          <button
            type='button'
            className='btn btn-icon d-flex align-items-center justify-content-center gap-1 p-0'
            style={{height: 52, width: 'auto', minWidth: 52}}
            title={`${userCount} project members`}
            aria-expanded={openPanel === 'users'}
            onClick={(e) => {
              e.stopPropagation()
              onTogglePanel('users')
            }}
          >
            <i className='fa-duotone fa-solid fa-users fs-2x' style={{color: color}} />
            <span className='fs-8 fw-bold text-dark'>{userCount}</span>
          </button>
        </div>

        <span className={`badge ${status.badge} fw-bold`}>{status.label}</span>

        <div style={{minWidth: '180px', flexShrink: 0}} title={`${percent}% elapsed`}>
          <div className='progress h-8px'>
            <div
              className='progress-bar'
              role='progressbar'
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{width: `${percent}%`, backgroundColor: color}}
            />
          </div>
        </div>
      </div>

      {openPanel && (
        <div className='card-body border-top pt-6'>
          {openPanel === 'info' && <ProjectInfoPanel project={project} />}
          {openPanel === 'users' && <ProjectUsersPanel project={project} />}
          {openPanel === 'week' && <ProjectGuestWeekPanel project={project} />}
        </div>
      )}
    </div>
  )
}

export function ProjectGuestMyProject() {
  const {projects, loading} = useProjectGuest()

  // /projects/me returns ProjectLightModel, which has no Users/Sites/Owner/Description/
  // Instructions — everything the count and the details popover need. So hydrate each one
  // from GET /projects/{id} (membership-checked, and a guest has only one or two projects).
  const [details, setDetails] = useState<any[]>([])
  const [loadingDetails, setLoadingDetails] = useState(false)

  // Which project has a panel open, and which panel. Held here rather than in ProjectRow so
  // the other rows can be dimmed to highlight the selected one.
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [openPanel, setOpenPanel] = useState<PanelKind>(null)

  // Auto-open the first project's week planner once, on initial load — a guest with one or two
  // projects lands straight on the thing they're here for instead of a wall of collapsed rows.
  const autoOpenedRef = useRef(false)

  useEffect(() => {
    if (loading) return

    if (projects.length === 0) {
      setDetails([])
      return
    }

    let cancelled = false
    setLoadingDetails(true)

    Promise.all(projects.map((p: any) => ProjectGuestPortalClient.GetProject(p.id))).then(
      (responses) => {
        if (cancelled) return

        // Fall back to the light model for any project whose detail fetch failed, so one bad
        // response can't blank the whole list.
        const mapped = responses.map((response, index) =>
          response.successful ? response.data : projects[index]
        )
        setDetails(mapped)
        setLoadingDetails(false)

        if (!autoOpenedRef.current && mapped.length > 0) {
          autoOpenedRef.current = true
          setExpandedId(mapped[0].id)
          setOpenPanel('week')
        }
      }
    )

    return () => {
      cancelled = true
    }
  }, [projects, loading])

  if (loading || loadingDetails) {
    return <div className='text-center py-10 text-muted'>Loading your projects...</div>
  }

  if (details.length === 0) {
    return (
      <div className='text-center py-10 text-muted'>
        You are not currently assigned to any project.
      </div>
    )
  }

  return (
    <div className='d-flex flex-column'>
      {details.map((project: any) => (
        <ProjectRow
          key={project.id}
          project={project}
          openPanel={expandedId === project.id ? openPanel : null}
          dimmed={expandedId !== null && expandedId !== project.id}
          onTogglePanel={(panel) => {
            // Clicking the same icon on the same row closes it; anything else moves the
            // open panel, so only ever one project is expanded at a time.
            const isSame = expandedId === project.id && openPanel === panel
            setExpandedId(isSame ? null : project.id)
            setOpenPanel(isSame ? null : panel)
          }}
        />
      ))}
    </div>
  )
}
